import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/AlternateEmail";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LanguageIcon from "@mui/icons-material/Language";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SearchIcon from "@mui/icons-material/Search";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { Box, Typography } from "@mui/material";
import { amber, blueGrey, green, indigo, lightBlue, purple, red } from "@mui/material/colors";
import { alpha, keyframes, type Theme } from "@mui/material/styles";
import type { SvgIconComponent } from "@mui/icons-material";
import { useEffect, useState } from "react";

import type { Decision, Speaker, ToolUse, Turn } from "./groupTurns.js";

/* ── Motion helpers (streaming reveal + live "thinking" dots) ─────── */

const blink = keyframes`0%,100% { opacity: 1 } 50% { opacity: 0 }`;
const dotPulse = keyframes`0%,80%,100% { opacity: 0.2 } 40% { opacity: 1 }`;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = (): void => setReduced(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);
  return reduced;
}

/**
 * Reveal `text` left-to-right like the agent is streaming it. Finishes in
 * ~1.6 s regardless of length (so long reflections don't crawl), and settles
 * to the full string immediately when disabled or when the viewer prefers
 * reduced motion.
 */
function useTypewriter(text: string, enabled: boolean): { shown: string; done: boolean } {
  const reduced = usePrefersReducedMotion();
  const animate = enabled && !reduced && text.length > 0;
  const [n, setN] = useState(animate ? 0 : text.length);
  useEffect(() => {
    if (!animate) {
      setN(text.length);
      return;
    }
    setN(0);
    let i = 0;
    const per = Math.max(1, Math.ceil(text.length / 100)); // ~100 frames ≈ 1.6 s
    const id = window.setInterval(() => {
      i = Math.min(text.length, i + per);
      setN(i);
      if (i >= text.length) window.clearInterval(id);
    }, 16);
    return () => window.clearInterval(id);
  }, [text, animate]);
  return { shown: text.slice(0, n), done: n >= text.length };
}

/** "thought for 3.2s" from a recorded reasoning latency (ms). */
export function thoughtForLabel(ms: number | undefined): string | null {
  if (!ms || ms < 250) return null;
  const s = ms / 1000;
  return `thought for ${s < 10 ? s.toFixed(1) : Math.round(s)}s`;
}

/* ── Speakers ─────────────────────────────────────────────────────── */

const SPEAKERS: Record<Speaker, { label: string; Icon: SvgIconComponent; hue: string }> = {
  master:    { label: "Master",    Icon: AutoAwesomeIcon,  hue: purple[500] },
  discovery: { label: "Discovery", Icon: SupportAgentIcon, hue: lightBlue[700] },
  system:    { label: "System",    Icon: PsychologyIcon,   hue: blueGrey[500] },
};

export function SpeakerAvatar({ speaker }: { speaker: Speaker }): JSX.Element {
  const s = SPEAKERS[speaker];
  return (
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        width: 30,
        height: 30,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(s.hue, 0.14),
        color: s.hue,
      }}
    >
      <s.Icon sx={{ fontSize: 17 }} />
    </Box>
  );
}

/* ── Thinking ─────────────────────────────────────────────────────── */

export function ThinkingBlock({
  text, animate = false,
}: { text: string; animate?: boolean }): JSX.Element {
  const { shown, done } = useTypewriter(text, animate);
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <PsychologyIcon sx={{ fontSize: 15, color: "text.disabled", mt: "1px" }} />
      <Typography
        sx={{
          fontSize: 13,
          fontStyle: "italic",
          lineHeight: 1.6,
          color: "text.secondary",
          minWidth: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {shown}
        {animate && !done ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              display: "inline-block",
              width: "0.5em",
              ml: "1px",
              borderRight: "2px solid",
              borderColor: "text.secondary",
              animation: `${blink} 1s step-end infinite`,
            }}
          />
        ) : null}
      </Typography>
    </Box>
  );
}

/**
 * The live "agent is thinking" row — shown at the top of the transcript while
 * the agent is mid-tick (its latest action is fresh). Animated dots, like
 * Claude's typing indicator.
 */
export function ThinkingIndicator({ speaker = "master" }: { speaker?: Speaker }): JSX.Element {
  return (
    <Box component="li" sx={{ display: "flex", gap: 1.25, listStyle: "none", alignItems: "center" }}>
      <SpeakerAvatar speaker={speaker} />
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.875,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.5,
          py: 1,
        }}
      >
        <Typography component="span" sx={{ fontSize: 12.5, fontStyle: "italic", color: "text.secondary" }}>
          thinking
        </Typography>
        <Box sx={{ display: "inline-flex", gap: 0.5 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              component="span"
              aria-hidden
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: "text.secondary",
                animation: `${dotPulse} 1.2s ease-in-out ${i * 0.18}s infinite`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ── Tool use ─────────────────────────────────────────────────────── */

function toolIcon(tool: string): SvgIconComponent {
  if (/(^|_)search$|web_search|^social|^news|kb_search/.test(tool)) return SearchIcon;
  if (tool === "fetch" || tool === "fetch_url") return LanguageIcon;
  if (tool === "enrich_org") return BusinessIcon;
  if (tool === "validate_email") return EmailIcon;
  if (tool === "extract_org" || tool === "write_lead") return PersonSearchIcon;
  if (tool === "write_brief") return DescriptionIcon;
  if (tool === "queue_mission") return PlaylistAddCheckIcon;
  if (tool === "open_investigation") return TravelExploreIcon;
  if (tool === "stop" || tool === "stop_for_now") return BlockIcon;
  return AutoAwesomeIcon;
}

export function ToolUseCard({ toolUse }: { toolUse: ToolUse }): JSX.Element {
  const Icon = toolIcon(toolUse.tool);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        alignSelf: "flex-start",
        maxWidth: "100%",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.04),
        px: 1.25,
        py: 0.5,
      }}
    >
      <Icon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
      <Typography component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, fontWeight: 600, color: "text.primary", flexShrink: 0 }}>
        {toolUse.tool}
      </Typography>
      {toolUse.arg ? (
        <Typography
          component="span"
          sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.secondary", overflowWrap: "anywhere", wordBreak: "break-word" }}
        >
          {toolUse.arg}
        </Typography>
      ) : null}
    </Box>
  );
}

/* ── Observation (what it saw) ────────────────────────────────────── */

export function ObservationCard({ observation }: { observation: Record<string, unknown> }): JSX.Element {
  const label = String(observation.label ?? "");
  const results = asRows(observation.results);
  const hits = asRows(observation.hits);
  const persons = asRows(observation.persons);
  const text = typeof observation.text === "string" ? observation.text : null;
  const title = typeof observation.title === "string" ? (observation.title as string) : null;
  const url = typeof observation.url === "string" ? (observation.url as string) : null;
  const list = results ?? hits;

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        px: 1.25,
        py: 0.75,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 0.25,
        "& > *": { minWidth: 0 },
      }}
    >
      {label ? <Typography sx={{ fontSize: 12.5, color: "text.primary", lineHeight: 1.5 }}>{label}</Typography> : null}
      {list && list.length > 0 ? (
        <Box component="ul" sx={{ listStyle: "none", m: 0, pl: 0, display: "grid", gap: 0.1 }}>
          {list.slice(0, 4).map((r, i) => (
            <Box component="li" key={i} sx={{ fontSize: 12, color: "text.secondary", overflowWrap: "anywhere", wordBreak: "break-word" }}>
              · {String(r.title ?? r.url ?? "(untitled)")}
            </Box>
          ))}
        </Box>
      ) : null}
      {persons && persons.length > 0 ? (
        <Box component="ul" sx={{ listStyle: "none", m: 0, pl: 0, display: "grid", gap: 0.1 }}>
          {persons.slice(0, 4).map((p, i) => (
            <Box component="li" key={i} sx={{ fontSize: 12, color: "text.secondary", overflowWrap: "anywhere", wordBreak: "break-word" }}>
              · {String(p.full_name ?? "(unnamed)")}
              {p.title ? <Box component="span" sx={{ color: "text.disabled" }}> — {String(p.title)}</Box> : null}
            </Box>
          ))}
        </Box>
      ) : null}
      {title && !list && !persons ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary", overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {title}{url ? ` · ${url}` : ""}
        </Typography>
      ) : null}
      {text && !list && !persons && !title ? (
        <Typography sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: "text.secondary", lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {text}
        </Typography>
      ) : null}
    </Box>
  );
}

function asRows(v: unknown): Array<Record<string, unknown>> | null {
  return Array.isArray(v) ? (v as Array<Record<string, unknown>>) : null;
}

/* ── Decision callout ─────────────────────────────────────────────── */

const DECISION: Record<Decision["kind"], { hue: string; Icon: SvgIconComponent; verb: string }> = {
  lead:        { hue: green[600],  Icon: CheckCircleIcon,      verb: "Lead captured" },
  stop:        { hue: amber[800],  Icon: BlockIcon,            verb: "Stopped" },
  queue:       { hue: indigo[500], Icon: PlaylistAddCheckIcon, verb: "Queued mission" },
  investigate: { hue: indigo[500], Icon: TravelExploreIcon,    verb: "Investigating" },
  brief:       { hue: purple[500], Icon: DescriptionIcon,      verb: "Wrote brief" },
  knowledge:   { hue: purple[400], Icon: AutoAwesomeIcon,      verb: "Refreshed self-knowledge" },
  strategy:    { hue: purple[500], Icon: AutoAwesomeIcon,      verb: "Regenerated strategy" },
  error:       { hue: red[600],    Icon: ErrorOutlineIcon,     verb: "Error" },
};

export function DecisionCallout({ decision }: { decision: Decision }): JSX.Element {
  const d = DECISION[decision.kind];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        alignSelf: "flex-start",
        maxWidth: "100%",
        borderRadius: 1,
        border: 1,
        borderColor: alpha(d.hue, 0.35),
        bgcolor: alpha(d.hue, 0.1),
        px: 1.25,
        py: 0.5,
      }}
    >
      <d.Icon sx={{ fontSize: 16, color: d.hue, flexShrink: 0 }} />
      <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: d.hue, flexShrink: 0 }}>
        {d.verb}
      </Typography>
      {decision.text ? (
        <Typography component="span" sx={{ fontSize: 12.5, color: "text.primary", overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {decision.text}
        </Typography>
      ) : null}
    </Box>
  );
}

/* ── Mission divider (a new "conversation") ───────────────────────── */

export function MissionDivider({ label }: { label: string | null }): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          px: 1.25,
          py: 0.25,
          color: "text.secondary",
        }}
      >
        <TravelExploreIcon sx={{ fontSize: 14 }} />
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {label || "Mission"}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
    </Box>
  );
}

/* ── One turn (a chat message) ────────────────────────────────────── */

export function ChatTurn({
  turn, animate = false,
}: { turn: Turn; animate?: boolean }): JSX.Element {
  const speaker = SPEAKERS[turn.speaker];
  const thought = thoughtForLabel(turn.latencyMs);
  return (
    <Box component="li" sx={{ display: "flex", gap: 1.25, listStyle: "none" }}>
      <SpeakerAvatar speaker={turn.speaker} />
      <Box sx={{ minWidth: 0, flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 0.75, "& > *": { minWidth: 0 } }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: "text.primary" }}>
            {speaker.label}
          </Typography>
          {turn.stepIndex ? (
            <Typography component="span" sx={{ fontSize: 10.5, color: "text.disabled" }}>step {turn.stepIndex}</Typography>
          ) : null}
          {turn.model ? (
            <Box
              component="span"
              title={`Reasoned by ${turn.model}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                borderRadius: 999,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.default",
                px: 0.75,
                py: 0.1,
                fontSize: 9.5,
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 11 }} />
              {turn.model}
            </Box>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {thought ? (
            <Typography
              component="span"
              title="How long the agent reasoned this turn"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, fontSize: 10.5, color: "text.disabled" }}
            >
              <PsychologyIcon sx={{ fontSize: 11 }} />
              {thought}
            </Typography>
          ) : null}
          <Typography component="span" sx={{ fontSize: 10.5, color: "text.disabled" }}>
            {new Date(turn.ts).toLocaleTimeString()}
          </Typography>
        </Box>
        {turn.thinking ? <ThinkingBlock text={turn.thinking} animate={animate} /> : null}
        {turn.toolUse ? <ToolUseCard toolUse={turn.toolUse} /> : null}
        {turn.observation ? <ObservationCard observation={turn.observation} /> : null}
        {turn.decision ? <DecisionCallout decision={turn.decision} /> : null}
      </Box>
    </Box>
  );
}
